import React, { useEffect, useState } from "react";
import { NFTExport } from "../../../../models/exports";

import Api from "../../../../services/api";
import AddNftCard from "./addNftCard";
import InventoryNftCard from "./inventoryNftCard";
import { useSelectedNfts } from "../../../../state/createAlbum/hooks";
import Modal from "../../modal/modal";
import ModalTitle from "../../modal/modalTitle";
import ModalSubtitle from "../../modal/modalSubtitle";
import Button from "../../button/button";
import { useProfile } from "../../../../hooks/profile/useProfile";
import { addressOrUsername } from "../../../../utils/profile/utils";
import { Link } from "gatsby";

import ScrollContainer from "react-indiana-drag-scroll";

const NFTSelector = ({
  walletAddress,
  tokenName,
  filter = "",
  transfer = false,
  addNft = () => {},
  removeNft = () => {},
  clearNft = () => {},
  selectedNFTs,
}: {
  walletAddress: string;
  tokenName: string;
  filter?: string;
  transfer?: boolean;
  addNft?: (nft?: NFTExport) => any;
  removeNft?: (nft?: NFTExport) => any;
  clearNft?: () => any;
  selectedNFTs: NFTExport[];
}) => {
  const [user, userLoading, userLoadingError] = useProfile(walletAddress);
  const [loading, setLoading] = useState<boolean>(true);
  const [walletNFTs, setWalletNFTs] = useState<NFTExport[] | undefined>(
    undefined
  );
  const [filteredNFTs, setFilteredNFTs] = useState<NFTExport[] | undefined>(
    undefined
  );

  const [inventoryNFTs, setInventoryNFTs] = useState<NFTExport[] | []>(
    new Array(50).fill(undefined)
  );
  const [showClearAllModal, setShowClearAllModal] = useState(false);

  const api = new Api();

  useEffect(() => {
    getAllNFTs(0, []).then((resp) => {
      setWalletNFTs(resp);
      setFilteredNFTs(resp);
      setLoading(false);
      window.scrollTo(0, 0);
    });
  }, []);

  const getAllNFTs = (cursor, calledNfts) =>
    api
      .getWalletNfts({ address: walletAddress, cursor })
      .then((call) => {
        let combinedNfts = calledNfts.concat(call.data.assets);
        //If we get a next field, it means we have more to load since api can only handle batches of 50
        if (call.data.next) {
          return getAllNFTs(call.data.next, combinedNfts);
        } else {
          return combinedNfts;
        }
      })
      .catch((err) => {
        console.log(err);
      });

  useEffect(() => {
    if (filter !== "" && walletNFTs) {
      let filteredList = walletNFTs.filter((walletNFT) => {
        let containsDescription = walletNFT.description
          ? walletNFT.description.toLowerCase().includes(filter.toLowerCase())
          : false;
        let containsName = walletNFT.name
          ? walletNFT.name.toLowerCase().includes(filter.toLowerCase())
          : false;
        let containsTypeName = walletNFT.type.name
          ? walletNFT.type.name.toLowerCase().includes(filter.toLowerCase())
          : false;

        return containsDescription || containsName || containsTypeName;
      });
      setFilteredNFTs(filteredList);
    } else {
      setFilteredNFTs(walletNFTs);
    }
  }, [filter]);

  const addSelectedNFT = (NFT: NFTExport) => {
    addNft(NFT);
  };

  const removeSelectedNFT = (NFT: NFTExport) => {
    removeNft(NFT);
  };

  //Every time the selected nft list updates
  //Rerender the list to display
  useEffect(() => {
    // if less than 50 items, we'll add undefined so we show blank slots on the inventory picker
    let resizedInventoryNFTs = new Array(50)
      .fill(undefined)
      .slice(selectedNFTs.length);
    let addedInventoryNFTs = selectedNFTs.concat(resizedInventoryNFTs);

    setInventoryNFTs(addedInventoryNFTs);
  }, [selectedNFTs]);

  const handleShowClearAllModal = () => {
    setShowClearAllModal(true);
  };

  const handleCloseClearAllModal = () => {
    setShowClearAllModal(false);
  };

  const clearInventoryNFTs = () => {
    setInventoryNFTs(new Array(50).fill(undefined));
    clearNft();
    handleCloseClearAllModal();
  };

  return (
    <>
      {loading ? (
        <div className="flex flex-col flex-grow justify-center items-start">
          <div className="loader bg-dark-divider py-3 px-5 rounded-full flex space-x-5 mx-auto">
            <div className="w-3 h-3 bg-dark-light-grey rounded-full animate-bounce"></div>
            <div className="w-3 h-3 bg-dark-light-grey rounded-full animate-bounce"></div>
            <div className="w-3 h-3 bg-dark-light-grey rounded-full animate-bounce"></div>
          </div>
        </div>
      ) : (
        <>
          <Modal isOpen={showClearAllModal} onClose={handleCloseClearAllModal}>
            <ModalTitle>
              <h2>This will clear your selected NFTs.</h2>
            </ModalTitle>
            <div className="flex flex-col space-y-24 ">
              <ModalSubtitle>Do you want to continue?</ModalSubtitle>
              <div className="flex flex-row mt-32 justify-between space-x-16">
                <Button
                  size="md"
                  width="full"
                  onClick={handleCloseClearAllModal}
                  fill={false}
                >
                  cancel
                </Button>
                <Button size="md" width="full" onClick={clearInventoryNFTs}>
                  clear all
                </Button>
              </div>
            </div>
          </Modal>
          <div className="flex flex-col h-full">
            {/* content */}
            <div className="flex flex-col flex-grow overflow-y-auto no-scrollbar pt-16">
              {!filteredNFTs || (filteredNFTs.length === 0 && filter === "") ? (
                <div className="flex flex-col items-center justify-center">
                  <h1 className="display1 text-white">Oops!</h1>
                  <p className="body1 text-dark-white-64 my-64">
                    We don’t see any NFTs in the connected wallet
                  </p>
                  <Link to="/explore">
                    <Button fill={false}>Explore albums</Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xlg:grid-cols-4 sm:gap-12 xlg:gap-24 mb-24">
                  {filteredNFTs.map((nft) => (
                    <AddNftCard
                      nft={nft}
                      selectedNFTs={selectedNFTs}
                      addNFT={addSelectedNFT}
                      removeNFT={removeSelectedNFT}
                      key={`${nft.type.contract}-${nft.id}`}
                    />
                  ))}
                </div>
              )}
              {filter !== "" ? (
                <div className="flex flex-col w-full text-center sm:py-36 xlg:py-64 text-dark-white-64">
                  <p className="body1">End of results.</p>
                  <p className="body1">Try broadening you query :)</p>
                </div>
              ) : null}
            </div>
            {/* bottom */}
            {filteredNFTs && filteredNFTs.length > 0 && (
              <div className="bg-black pb-16 rounded-t-sm pl-16 pt-16 border border-dark-divider">
                <div className="flex flex-row flex-start items-center mr-16">
                  {!transfer ? (
                    <h2 className="text-dark-light-grey">
                      {`$${tokenName.toUpperCase()}`} by{" "}
                      {addressOrUsername(user)} •{" "}
                      {selectedNFTs ? selectedNFTs.length : 0}{" "}
                      {selectedNFTs &&
                      (selectedNFTs.length === 0 || selectedNFTs.length > 1)
                        ? "NFTs"
                        : "NFT"}
                    </h2>
                  ) : (
                    <h2 className="text-dark-light-grey">
                      {selectedNFTs ? selectedNFTs.length : 0}{" "}
                      {selectedNFTs &&
                      (selectedNFTs.length === 0 || selectedNFTs.length > 1)
                        ? "NFTs"
                        : "NFT"}
                    </h2>
                  )}
                  {!transfer && (
                    <p className="body1 text-dark-white-64 ml-32">
                      NFTs will appear in the album in the order you select
                    </p>
                  )}
                  <button
                    type="button"
                    disabled={selectedNFTs.length === 0}
                    className={
                      (selectedNFTs.length === 0 ? "text-opacity-50 " : "") +
                      "btn-second text-dark-red content-center focus:ring-transparent focus:outline-none ml-auto"
                    }
                    onClick={handleShowClearAllModal}
                  >
                    Clear all
                  </button>
                </div>
                <ScrollContainer className="flex-grow grid grid-rows-1 grid-flow-col overflow-hidden overflow-x-auto no-scrollbar gap-12 justify-start scroll-container">
                  {inventoryNFTs.map((nft, index) => (
                    <InventoryNftCard
                      nft={nft}
                      removeNFT={removeSelectedNFT}
                      key={
                        nft
                          ? `${nft.type.contract}-${nft.id}`
                          : `${index}-${Date.now().toString().slice(-4)}`
                      }
                    />
                  ))}
                </ScrollContainer>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default NFTSelector;
